import { Chip, Tooltip } from "@mui/material";
import { ExceptionGroupExtensionStatus, ExceptionStatus } from "~/operations";
import { useColorMode } from "~/providers/color-mode";
import RadioButtonCheckedOutlinedIcon from "@mui/icons-material/RadioButtonCheckedOutlined";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";

interface StatusConfig {
  color: { dark: string; light: string };
  label: string;
  activeText: string;
  activeState: string;
}

const getStatusConfig = (
  status: ExceptionStatus | ExceptionGroupExtensionStatus,
  isActive: boolean,
): StatusConfig => {
  const activeState = isActive ? "on" : "off";
  let color: { dark: string; light: string };
  let label: string;
  let activeText: string;

  switch (status) {
    case ExceptionGroupExtensionStatus.NotReviewed:
    case ExceptionStatus.New:
      color = { dark: "#8E7A3C", light: "#FDE9AC" };
      label = "Needs review";
      activeText = "Needs review";
      break;
    case ExceptionStatus.NeedsExtension:
      color = { dark: "#8E7A3C", light: "#FDE9AC" };
      label = "Needs extension";
      activeText = "Needs Review for Extension";
      break;
    case ExceptionGroupExtensionStatus.Approved:
    case ExceptionStatus.Approved:
      color = { dark: "#2C7256", light: "#9BE1C6" };
      label = "Approved";
      activeText = "Approved";
      break;
    case ExceptionStatus.Expired:
      color = { dark: "#363635", light: "#F0F0F0" };
      label = "Expired";
      activeText = "Expired";
      break;
    case ExceptionStatus.Removed:
      color = { dark: "#363635", light: "#F0F0F0" };
      label = "Removed";
      activeText = "Removed";
      break;
    case ExceptionGroupExtensionStatus.Rejected:
    case ExceptionStatus.Rejected:
      color = { dark: "#363635", light: "#F0F0F0" };
      label = "Rejected";
      activeText = "Rejected";
      break;
  }

  return {
    color,
    label,
    activeText,
    activeState,
  };
};

interface ReviewStatusChipProps {
  status: ExceptionStatus | ExceptionGroupExtensionStatus;
  isActive: boolean;
  isRemoved: boolean;
}

export function ReviewStatusChip({
  status,
  isActive,
  isRemoved,
}: ReviewStatusChipProps) {
  const { mode } = useColorMode();
  const isDarkMode = mode === "dark";
  const config = getStatusConfig(status, isActive);
  const backgroundColor = isDarkMode ? config.color.dark : config.color.light;

  const tooltipLabel = (
    <>
      <strong>{config.activeText}</strong>: This exception is{" "}
      {config.activeState}
    </>
  );

  return (
    <Tooltip title={tooltipLabel} placement="top" arrow>
      <Chip
        color="primary"
        icon={
          isActive ? (
            <RadioButtonCheckedOutlinedIcon />
          ) : (
            <RadioButtonUncheckedOutlinedIcon />
          )
        }
        label={config.label}
        disabled={status === ExceptionStatus.Removed}
        size="small"
        sx={{
          borderRadius: "8px",
          fontSize: 12,
          fontWeight: 600,
          padding: "6px 8px",
          height: "auto",
          gap: 1,
          backgroundColor,
          color: "inherit",
          opacity: isRemoved ? 0.5 : 1,
        }}
      />
    </Tooltip>
  );
}
